import { Input } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useChannelContext } from "@sendbird/uikit-react/Channel/context";
import { useRef, useState } from "react";

import { MESSAGE_INPUT_SPACING } from "../../Chat/constants";
import { useToast } from "../../lib";
import { IconButton } from "../components/IconButton";
import { QuoteReply } from "./QuoteReply";

interface CustomMessageInputProps {
  prefilledMessage?: string;
}

export function CustomMessageInput({ prefilledMessage }: CustomMessageInputProps) {
  const { sendMessage, sendFileMessage, currentGroupChannel, quoteMessage, setQuoteMessage } =
    useChannelContext();

  const [messageInput, setMessageInput] = useState(prefilledMessage ?? "");
  const { showErrorToast } = useToast();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  const chatContainer = document.querySelector(
    ".chat-container .sendbird-conversation__messages-padding"
  );

  const canSend = messageInput.trim().length > 0;

  const hideKeyboard = () => {
    textInputRef.current?.blur();
  };

  const handleSendMessage = () => {
    if (!canSend) {
      return;
    }

    try {
      sendMessage({ message: messageInput, ...(isDefined(quoteMessage) && { quoteMessage }) });
      setMessageInput("");
      void currentGroupChannel?.endTyping?.();
      setQuoteMessage(null);
    } catch {
      showErrorToast("There was an error sending your message. Please try again later.");
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const { files } = event.target;

      if (isDefined(files) && files.length > 0) {
        const file = files[0];
        await sendFileMessage(file);
      }
    } catch {
      showErrorToast("There was an error sending your file. Please try again later.");
    }
  };

  const handleClickAttachFile = () => {
    fileInputRef.current?.click();
  };

  const handleInputFocus = () => {
    chatContainer?.addEventListener("touchmove", hideKeyboard, { passive: true, once: true });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    void currentGroupChannel?.startTyping?.();
    setMessageInput(event.target.value);
  };

  const handleCloseQuoteReply = () => {
    setQuoteMessage(null);
  };

  return (
    <Stack paddingX={MESSAGE_INPUT_SPACING} sx={{ boxShadow: (theme) => theme.shadows[4] }}>
      {isDefined(quoteMessage) && (
        <QuoteReply quoteMessage={quoteMessage} onClose={handleCloseQuoteReply} />
      )}

      <Stack spacing={2} direction="row" alignItems="center" paddingTop={MESSAGE_INPUT_SPACING}>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          aria-label="file-input"
          onChange={handleFileSelect}
        />
        <IconButton
          iconType="paperclip"
          variant="outlined"
          aria-label="attach-file"
          onClick={handleClickAttachFile}
        />
        <Input
          inputRef={textInputRef}
          placeholder="Message"
          value={messageInput}
          aria-label="chat-text-input"
          onEnterPress={handleSendMessage}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
        />
        <IconButton
          iconType="arrow-up"
          variant="contained"
          aria-label="send"
          disabled={!canSend}
          onClick={handleSendMessage}
        />
      </Stack>
    </Stack>
  );
}
